/* ============================================================================
   IMPRESSION — genera el archivo de impresion a 300 DPI desde el design JSON
   ----------------------------------------------------------------------------
   Entrada: el JSON que produce el Studio (construireJSON, version 2).
   Salida:  un PNG por zona, al tamano real  cm x 300 / 2.54.

   Reglas de produccion:
     - SIN mockup y SIN textura de tejido. La textura es un efecto de pantalla
       para que el cliente se haga una idea; imprimirla arruinaria el trabajo.
     - Fondo transparente, salvo que la zona lleve color de fondo (fondAutorise).
     - Fuera de la forma, transparente: un porte-cle redondo no puede salir
       cuadrado.
     - Las fotos se componen desde el ORIGINAL cuando esta disponible, con su
       recorte aplicado en pixeles del original, no desde la miniatura del canvas.
     - Las fuentes se cargan ANTES de dibujar. Si no, el navegador pinta con la
       de reemplazo y el archivo sale con otra tipografia sin avisar.

   POR QUE SE RENDERIZA CON FABRIC Y NO CON CANVAS 2D A PELO: la primera
   version reimplementaba la maquetacion de texto con fillText, y el resultado
   salia 52 px (4 mm) mas arriba que en pantalla, porque Fabric centra la CAJA
   de texto (ascendente + descendente + interlineado) y fillText centra la
   TINTA. Parchear ese offset habria arreglado un caso y roto el siguiente
   (multilinea, espaciado, alineacion). Aqui se reconstruyen objetos de Fabric
   a escala de impresion y se deja maquetar al MISMO motor que dibuja en el
   Studio: la divergencia deja de ser posible por construccion.

   Lo que este modulo NO hace: rotacion de zona (aun no existe) ni composicion
   del modelo base (sistema de modelos). Cuando lleguen, la rotacion se aplica
   "al reves" aqui: el archivo de impresion siempre sale derecho.
   ========================================================================== */

const IMPRESSION = (() => {

  const cargarImagen = src => new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => res(i);
    i.onerror = () => rej(new Error('image illisible'));
    i.src = src;
  });

  /* Path de la forma de la zona, en pixeles del archivo de impresion. Se
     reutiliza el catalogo de formes.js escalando su path normalizado. */
  function pathDeZona(zona, W, H) {
    /* VERTICES PROPIOS PRIMERO. Si Franklin ajusto a mano los seis puntos del
       hexagono sobre la foto, el recorte de IMPRESION tiene que ser ese y no
       el hexagono perfecto de la familia: si no, la pantalla ensena una forma
       y la cortadora recibe otra. Vienen en fracciones 0..1 de la zona. */
    const pts = zona.sommets;
    if (Array.isArray(pts) && pts.length >= 3) {
      return new Path2D('M ' + pts.map(([x, y]) =>
        `${(x * W).toFixed(3)} ${(y * H).toFixed(3)}`).join(' L ') + ' Z');
    }
    const forme = zona.forme || 'rect';
    if (forme === 'rect') return null;                  // sin recorte
    const f = (typeof FORMES !== 'undefined') && FORMES[forme];
    if (!f || !f.path) return null;
    const d = transformarPath(f.path, 0, 0, W, H);      // de formes.js
    return new Path2D(d);
  }

  /* El agujero de la anilla no se imprime: se perfora del archivo. */
  function perforarAgujero(ctx, zona, W, H) {
    if (!zona.trou) return;
    const pxParCmX = W / zona.cm.w, pxParCmY = H / zona.cm.h;
    const d  = (zona.trou.dCm || 0.4) * pxParCmX;
    const cx = zona.trou.xCm != null ? zona.trou.xCm * pxParCmX : W / 2;
    const cy = zona.trou.yCm != null ? zona.trou.yCm * pxParCmY : d;
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath(); ctx.arc(cx, cy, d / 2, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }

  /* Carga las familias que usa el diseno y ESPERA a que esten listas. */
  async function cargarFuentes(json) {
    const familias = new Set();
    json.vues.forEach(v => v.zones.forEach(z => (z.elements || []).forEach(e => {
      if (e.type === 'texte' && e.familleCss) familias.add(e.familleCss);
    })));
    await Promise.all([...familias].map(async fam => {
      const limpia = String(fam).split(',')[0].replace(/["']/g, '').trim();
      if (typeof chargerPolice === 'function') { try { await chargerPolice(limpia); } catch (e) {} }
      if (document.fonts) { try { await document.fonts.load(`400 64px "${limpia}"`); } catch (e) {} }
      if (document.fonts) { try { await document.fonts.load(`700 64px "${limpia}"`); } catch (e) {} }
    }));
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
    return [...familias];
  }

  /* Reconstruye un elemento del JSON como objeto de Fabric, ya a escala de
     impresion. Las coordenadas del JSON son normalizadas 0..1 respecto a la
     zona y apuntan al CENTRO del elemento, que es justo el origen que usa el
     Studio (originX/originY: 'center'). */
  async function objetoFabric(e, W, H, pxParCm, avisos) {
    const comun = {
      left: e.centre.x * W, top: e.centre.y * H,
      originX: 'center', originY: 'center',
      angle: e.angle || 0,
      flipX: !!(e.miroir && e.miroir.x), flipY: !!(e.miroir && e.miroir.y),
      opacity: e.opacite == null ? 1 : e.opacite,
    };

    if (e.type === 'texte') {
      const px = (e.tailleTexte && e.tailleTexte.pxImpression) || Math.round(e.taille.h * H);
      const t = new fabric.Textbox(String(e.contenu || ''), Object.assign({}, comun, {
        width: e.taille.w * W,          // misma caja que en pantalla -> mismo salto de linea
        fontSize: px,
        fontFamily: e.familleCss || 'sans-serif',
        fill: e.couleur || '#000',
        textAlign: e.alignement || 'center',
        fontWeight: e.graisse || 'normal',
        fontStyle: e.style || 'normal',
        charSpacing: e.espacementLettres || 0,
        /* El contorno se guarda en cm reales: hay que reescalarlo a 300 DPI,
           no copiar los pixeles de pantalla. */
        stroke: (e.contour && e.contour.couleur) || null,
        strokeWidth: (e.contour && e.contour.epaisseurCm > 0)
          ? e.contour.epaisseurCm * pxParCm : 0,
        paintFirst: 'stroke',
        strokeLineJoin: 'round',
      }));
      return t;
    }

    /* Un QR se REHACE aqui a la resolucion de impresion. Ver imagenQR. */
    if (e.qr && e.qr.url) {
      const qimg = await imagenQR(e, W, H);
      if (qimg) {
        const q = new fabric.Image(qimg, comun);
        q.set({ scaleX: (e.taille.w * W) / qimg.naturalWidth,
                scaleY: (e.taille.h * H) / qimg.naturalHeight });
        return q;
      }
      avisos.push('Code QR non régénéré : la version d\u2019aperçu a été utilisée, '
                + 'vérifiez qu\u2019il se scanne avant de produire.');
    }

    /* Un simbolo se REDIBUJA como vector a la resolucion de impresion. Igual
       que el QR y por lo mismo: estirar el bitmap del canvas daria un borde
       dentado en un corazon de 8 cm. */
    if (e.type === 'forme' && e.symbole && typeof SYMBOLES !== 'undefined') {
      const def = SYMBOLES.parId(e.symbole.id);
      if (def) {
        const p = new fabric.Path(def.d, Object.assign({}, comun, {
          fill: e.symbole.trait ? '' : (e.symbole.couleur || '#000'),
          stroke: e.symbole.trait ? (e.symbole.couleur || '#000') : null,
          /* El grosor se guarda en la escala del canvas; hay que llevarlo a la
             de impresion o el marco saldria con un hilo invisible. */
          strokeWidth: e.symbole.trait
            ? Math.max(1, (e.symbole.epaisseur || 7) * ((e.taille.w * W) / 100))
            : 0,
          strokeLineJoin: 'round', strokeUniform: true,
          /* El path vive en la caja 0..100: se escala al tamano pedido. */
          scaleX: (e.taille.w * W) / 100,
          scaleY: (e.taille.h * H) / 100,
        }));
        return p;
      }
      avisos.push('Forme inconnue ignorée : ' + e.symbole.id);
      return null;
    }

    const src = (e.source && (e.source.dataUrlOriginale || e.source.dataUrl)) || null;
    if (!src) { avisos.push('Élément image sans source : ignoré.'); return null; }
    if (e.source && !e.source.origineDisponible)
      avisos.push('Photo composée depuis la miniature (original absent) : résolution réduite.');

    const img = await cargarImagen(src);
    const usaOriginal = !!(e.source && e.source.dataUrlOriginale);
    const r = (e.recadrage && usaOriginal) ? e.recadrage : null;

    const fImg = new fabric.Image(img, comun);

    /* Si la foto vive en un emplacement, se recorta al MARCO. El marco viene
       normalizado a la zona, asi que a 300 DPI se reconstruye igual que en
       pantalla. Sin este recorte la foto saldria entera y desbordando. */
    if (e.emplacement && e.emplacement.cadre) {
      const c = e.emplacement.cadre;
      const comun = { left: (c.x + c.w/2) * W, top: (c.y + c.h/2) * H,
                      originX:'center', originY:'center', absolutePositioned:true };
      /* La forma del hueco viaja en el JSON. Si el export recortara siempre
         en rectangulo, una foto que en pantalla sale redonda se imprimiria
         cuadrada: el preview estaria mintiendo. */
      fImg.clipPath = (e.emplacement.forme === 'cercle')
        ? new fabric.Circle(Object.assign(
            { radius: Math.min(c.w * W, c.h * H) / 2 }, comun))
        : new fabric.Rect(Object.assign(
            { width: c.w * W, height: c.h * H }, comun));
    }
    if (r) fImg.set({ cropX: r.x, cropY: r.y, width: r.w, height: r.h });
    const anchoFuente = r ? r.w : img.naturalWidth;
    const altoFuente  = r ? r.h : img.naturalHeight;
    fImg.set({ scaleX: (e.taille.w * W) / anchoFuente,
               scaleY: (e.taille.h * H) / altoFuente });
    return fImg;
  }

  /* Un QR NO se estira: se REHACE a la resolucion final. Estirar la vista
     previa da modulos borrosos y un codigo que el telefono lee mal o no lee —
     y eso no se descubre hasta tener el producto impreso en la mano. */
  async function imagenQR(e, W, H) {
    if (!e.qr || !e.qr.url || typeof QR === 'undefined') return null;
    const px = Math.max(256, Math.round(e.taille.w * W));
    try {
      const dataUrl = await QR.generer(e.qr.url, e.qr, px);
      return await cargarImagen(dataUrl);
    } catch (err) {
      console.warn('[EDISAMP] QR impression:', err);
      return null;
    }
  }

  /* Genera UNA zona. Devuelve { canvas, avisos }. */
  async function genererZone(json, vue, zona) {
    const W = zona.impressionPx.w, H = zona.impressionPx.h;

    /* SIN LOS DOS LADOS EN CENTIMETROS NO HAY FICHERO QUE VALGA.
       -----------------------------------------------------------------------
       Desde que la altura fisica puede ser DESCONOCIDA —el admin ya no la
       deduce de la anchura—, una zona sin calibracion vertical llega aqui con
       `cm.h` vacio, y `Math.round(undefined * 300)` da NaN. Un canvas de NaN
       px no falla: sale, vacio, y el fichero se manda a imprimir. Mejor que
       reviente aqui, con el nombre de la zona, que en la imprenta. */
    const dims = [['largeur', zona.cm && zona.cm.w, W], ['hauteur', zona.cm && zona.cm.h, H]];
    const falta = dims.filter(d => !(d[1] > 0) || !(d[2] > 0)).map(d => d[0]);
    if (falta.length) {
      throw new Error(`Zone « ${zona.nom || zona.zone} » : ${falta.join(' et ')} `
        + `en centimètres inconnue(s). Le produit n’est pas calibré `
        + `${falta.includes('hauteur') ? 'verticalement ' : ''}dans l’admin — `
        + `« Calibrer sur la photo », puis relancez le fichier d’impression.`);
    }

    const pxParCm = W / zona.cm.w;
    const avisos = [];

    /* 1. Maquetacion con Fabric, a escala de impresion. */
    const el = document.createElement('canvas');
    el.width = W; el.height = H;
    const fc = new fabric.StaticCanvas(el, { width: W, height: H, backgroundColor: null,
                                             enableRetinaScaling: false });
    if (zona.fond) fc.setBackgroundColor(zona.fond);

    for (const e of (zona.elements || [])) {
      try {
        const o = await objetoFabric(e, W, H, pxParCm, avisos);
        if (o) fc.add(o);
      } catch (err) { avisos.push(`Élément ignoré : ${err.message}`); }
    }
    fc.renderAll();

    /* 2. Recorte a la forma y perforado de la anilla sobre el resultado. */
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const ctx = c.getContext('2d');
    const recorte = pathDeZona(zona, W, H);
    ctx.save();
    if (recorte) ctx.clip(recorte);
    ctx.drawImage(fc.lowerCanvasEl, 0, 0, W, H);
    ctx.restore();
    perforarAgujero(ctx, zona, W, H);

    fc.dispose();
    return { canvas: c, avisos, nom: `${json.produit.handle}_${vue.vue}_${zona.zone}_${W}x${H}` };
  }

  /* ------------------------- GRAVURE (laser / Cricut) ---------------------
     Un laser no imprime color: quema. Lo que necesita es un mapa de CUANTO
     quemar en cada punto, y el convenio de Cricut y compania es negro puro
     sobre transparente — cuanto mas opaco, mas profundo.

     Por eso la conversion NO es « pintar de negro lo que no sea transparente »:
     eso convertiria una foto en una mancha. Se pasa a luminancia y se usa lo
     OSCURO como opacidad. Un texto negro sale solido, una foto sale con sus
     medios tonos, y el papel blanco del diseno —lo claro— desaparece, que es
     justo lo que no hay que quemar.

     El PNG normal se sigue generando igual: este es un fichero ADEMAS. */
  function versGravure(origen) {
    const c = document.createElement('canvas');
    c.width = origen.width; c.height = origen.height;
    const x = c.getContext('2d');
    x.drawImage(origen, 0, 0);
    const d = x.getImageData(0, 0, c.width, c.height);
    const p = d.data;
    for (let i = 0; i < p.length; i += 4) {
      const a = p[i+3];
      if (!a) { p[i] = p[i+1] = p[i+2] = 0; continue; }
      /* Luminancia percibida (Rec. 601): el ojo no pesa igual los tres
         canales, y un rojo saturado debe quemar mas que un amarillo. */
      const lum = (0.299*p[i] + 0.587*p[i+1] + 0.114*p[i+2]) / 255;
      p[i] = p[i+1] = p[i+2] = 0;
      p[i+3] = Math.round((1 - lum) * a);
    }
    x.putImageData(d, 0, 0);
    return c;
  }

  /* Genera TODO el diseno. Carga las fuentes una sola vez, antes de dibujar. */
  async function generer(json) {
    if (!json || json.version !== 2) throw new Error('JSON de design non reconnu (version 2 attendue).');
    if (typeof fabric === 'undefined')
      throw new Error('Fabric.js n\u2019est pas chargé : impossible de composer le fichier.');
    const familias = await cargarFuentes(json);
    /* Si el diseno lleva QR, la libreria tiene que estar antes de componer. */
    const hayQR = json.vues.some(v => (v.zones||[]).some(z =>
      (z.elements||[]).some(e => e.qr && e.qr.url)));
    if (hayQR && typeof QR !== 'undefined') await QR.charger();
    const salidas = [];
    for (const v of json.vues)
      for (const z of v.zones) {
        const f = await genererZone(json, v, z);
        salidas.push(f);
        /* En gravure salen DOS ficheros por zona: el normal, para ver y para
           archivo, y el de laser. */
        if ((json.produit || {}).finition === 'gravure') {
          salidas.push({
            canvas: versGravure(f.canvas),
            avisos: [],
            gravure: true,
            nom: f.nom + '_GRAVURE-laser',
          });
        }
      }
    return { fichiers: salidas, polices: familias };
  }

  const descargar = (canvas, nom) => new Promise(res => {
    canvas.toBlob(b => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(b);
      a.download = nom + '.png';
      a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); res(); }, 1000);
    }, 'image/png');
  });

  return { generer, genererZone, descargar, cargarFuentes };
})();
